import { collection, setDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import { useCallback, useContext } from 'react';
import { InInUser, UserContext } from 'components/UserContext';
import { getAuth, signOut, User } from "firebase/auth";
import { toast } from 'react-toastify';
import { ModalContext } from 'components/ModalContext';
import { SignInModal } from 'components/Modals/SignInModal';
import { useEffect } from 'react';
import { firestore } from 'fb';
import { useMutation, useQuery, useQueryClient } from "react-query";

export type SignOutOptions = {
  redirectTo?: string;
};

export const useSignOut = ({ redirectTo = '/' }: SignOutOptions = {}) => {
  const navigate = useNavigate();
  return useCallback(() => {
    const auth = getAuth();

    signOut(auth).then(() => {
      navigate(redirectTo);
    });
  }, [navigate, redirectTo]);
};

export const useCheckLogin = () => {
  const { isLoading, user } = useContext(UserContext);
  const { open } = useContext(ModalContext);

  return useCallback(() => {
    if (isLoading) {
      toast.info('로그인 확인 중입니다. 잠시 후 다시 시도해 주세요.');
      return false;
    }

    if (!user) {
      open(<SignInModal />);
      return false;
    }

    return true;
  }, [isLoading, open, user]);
}

export const useCreateUser = (user?: User) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!user) {
      return;
    }

    getInInUser(user.uid).then(async inInUser => {
      if (!inInUser) {
        const data = {
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
          displayName: user.displayName,
          photoURL: user.photoURL || 'https://firebasestorage.googleapis.com/v0/b/theinin.appspot.com/o/default.png?alt=media&token=f54dde77-36e6-41d5-a5eb-86be0b603e4c',
          phoneNumber: user.phoneNumber,
          creationTime: user.metadata.creationTime,
          lastSignInTime: user.metadata.lastSignInTime,
          opinionLikes: [],
          opinionReports: [],
          canReadArticles: false,
          canReadReason: null,
        };

        await setDoc(doc(collection(firestore, 'users'), user.uid), data);
        queryClient.invalidateQueries('me');
      }
    });
  }, [queryClient, user]);
}

export const getInInUser = async (uid: string) => {
  const docRef = doc(firestore, 'users', uid);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    return null;
  }

  const d = docSnap.data();

  return {
    ...d,
    displayName: d?.displayName || d?.email?.split('@')[0],
  } as InInUser;
}

export const useUserQuery = (uid: string) => useQuery(['users', uid], () => getInInUser(uid));

export const useUserMutation = () => {
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();
  return useMutation(async (data: Partial<InInUser>) => {
    if (!user) {
      throw new Error('Cannot mutate user without user');
    }

    const docRef = doc(firestore, 'users', user.uid);
    await updateDoc(docRef, data);
  }, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('me');
    },
  });
};
